import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};
const RED = '#F87171';
const AMBER = '#F5A623';

interface IntentCohort {
  choice: string; chose: number; saw_paywall: number;
  trials: number; cancelled: number; active: number; paying: number;
}

const LABELS: Record<string, string> = {
  both: 'Both (identify + care)',
  identify: 'Identify',
  care: 'Care',
  '(no answer)': 'No answer',
};

function pct(n: number, d: number): string {
  return d > 0 ? `${Math.round((n / d) * 100)}%` : '—';
}

export default function CohortsTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [rows, setRows] = useState<IntentCohort[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/onboarding/cohorts`, { headers: authHeaders(token) });
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem(TOKEN_KEY);
        window.location.reload();
        return;
      }
      if (!res.ok) { setError(`Server error (${res.status}).`); return; }
      const d = await res.json();
      setRows(((d.data ?? d).intent ?? []) as IntentCohort[]);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const totals = (rows ?? []).reduce(
    (a, r) => ({
      chose: a.chose + r.chose, saw_paywall: a.saw_paywall + r.saw_paywall,
      trials: a.trials + r.trials, cancelled: a.cancelled + r.cancelled,
      active: a.active + r.active, paying: a.paying + r.paying,
    }),
    { chose: 0, saw_paywall: 0, trials: 0, cancelled: 0, active: 0, paying: 0 },
  );

  const cell: React.CSSProperties = { padding: '13px 12px', whiteSpace: 'nowrap' };
  const th: React.CSSProperties = { padding: '12px 12px', fontWeight: 600, textAlign: 'left', color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div style={{ paddingBottom: 40 }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Intent Cohorts</h1>
      <p style={{ color: C.sub, marginTop: 6, fontSize: 15, maxWidth: 700 }}>
        The full funnel by onboarding choice ("What brings you to Gardeno?"): how many chose it, reached the paywall, dropped, started a trial, then cancelled or paid. Trials are real Apple trials only — the Trial column matches "Trials Started" on the overview.
      </p>

      {error && <p style={{ color: RED, marginTop: 16 }}>{error}</p>}
      {loading && !rows && <p style={{ color: C.sub, marginTop: 24 }}>Loading…</p>}

      {rows && (
        <div style={{ marginTop: 20, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 820 }}>
              <thead>
                <tr style={{ background: C.panelAlt }}>
                  <th style={th}>Onboarding choice</th>
                  <th style={{ ...th, textAlign: 'right' }}>Chose</th>
                  <th style={{ ...th, textAlign: 'right' }}>Saw paywall</th>
                  <th style={{ ...th, textAlign: 'right' }}>Dropped</th>
                  <th style={{ ...th, textAlign: 'right' }}>Trial</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cancelled</th>
                  <th style={{ ...th, textAlign: 'right' }}>Paying</th>
                  <th style={{ ...th, textAlign: 'right' }}>Paywall→Trial</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cancel rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const cr = r.trials > 0 ? r.cancelled / r.trials : 0;
                  const dropped = Math.max(r.saw_paywall - r.trials, 0);
                  const noTop = r.chose === 0;
                  return (
                    <tr key={r.choice} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ ...cell, fontWeight: 700 }}>{LABELS[r.choice] ?? r.choice}</td>
                      <td style={{ ...cell, textAlign: 'right', color: noTop ? C.sub : C.text }}>{noTop ? '—' : r.chose}</td>
                      <td style={{ ...cell, textAlign: 'right', color: noTop ? C.sub : C.text }}>{noTop ? '—' : r.saw_paywall}</td>
                      <td style={{ ...cell, textAlign: 'right', color: noTop ? C.sub : AMBER }}>{noTop ? '—' : dropped}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{r.trials}</td>
                      <td style={{ ...cell, textAlign: 'right', color: r.cancelled > 0 ? RED : C.sub }}>{r.cancelled}</td>
                      <td style={{ ...cell, textAlign: 'right', color: r.paying > 0 ? C.accent : C.sub, fontWeight: r.paying > 0 ? 700 : 400 }}>{r.paying}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: noTop ? C.sub : C.accent }}>{noTop ? '—' : pct(r.trials, r.saw_paywall)}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: cr >= 0.6 ? RED : cr >= 0.5 ? AMBER : C.accent }}>{pct(r.cancelled, r.trials)}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>No trial data yet.</td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${C.border}`, background: C.panelAlt }}>
                    <td style={{ ...cell, fontWeight: 800 }}>Total</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{totals.chose}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{totals.saw_paywall}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: AMBER }}>{Math.max(totals.saw_paywall - totals.trials, 0)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{totals.trials}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: RED }}>{totals.cancelled}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: C.accent }}>{totals.paying}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: C.accent }}>{pct(totals.trials, totals.saw_paywall)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{pct(totals.cancelled, totals.trials)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {rows && rows.length > 0 && (
        <p style={{ color: C.sub, fontSize: 12.5, marginTop: 14, maxWidth: 640, lineHeight: 1.5 }}>
          "Chose" &amp; "Saw paywall" count devices in onboarding; "Dropped" = saw the paywall but didn't start a trial. "Paywall→Trial" is trials ÷ saw paywall. "Cancelled" = auto-renew turned off at any point (a subset of Trials); "Paying" = past the trial and charged. Signed-in users are matched to their onboarding answer through their scan history, so Apple/Google sign-ups are included.
        </p>
      )}
    </div>
  );
}
